"use client";

import {Box, Button, FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {useRouter, useSearchParams} from "next/navigation";
import ClearIcon from "@mui/icons-material/Clear";
import {PressCategory, PressSource} from "~/features/press/model";
import {PRESS_CATEGORY_LABELS, PRESS_SOURCE_LABELS,} from "~/features/press/labels";
import {currentLocale, SupportedLocale} from "~/core/i18n/locales";

export const PRESS_SOURCES = Object.keys(
  PRESS_SOURCE_LABELS
) as PressSource[];

export const PRESS_CATEGORIES = Object.keys(
  PRESS_CATEGORY_LABELS
) as PressCategory[];

export type PressFilter = {
  source?: PressSource;
  category?: PressCategory;
};

export function isPressSource(value: string | null): value is PressSource {
  return !!value && PRESS_SOURCES.includes(value as PressSource);
}

export function isPressCategory(value: string | null): value is PressCategory {
  return !!value && PRESS_CATEGORIES.includes(value as PressCategory);
}

export function parsePressFilter(
  searchParams: URLSearchParams
): PressFilter {
  const rawSource = searchParams.get("source");
  const rawCategory = searchParams.get("category");

  return {
    source: isPressSource(rawSource) ? rawSource : undefined,
    category: isPressCategory(rawCategory) ? rawCategory : undefined,
  };
}

export function PressFilterBar() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const {source, category} = parsePressFilter(searchParams);

  const isFiltered = !!source || !!category;

  function update(
    nextSource: PressSource | "ALL",
    nextCategory: PressCategory | "ALL"
  ) {
    const params = new URLSearchParams();

    if (nextSource !== "ALL") params.set("source", nextSource);
    if (nextCategory !== "ALL") params.set("category", nextCategory);

    router.push(`/press?${params.toString()}`);
  }

  function getPressSourceOptions(locale: SupportedLocale) {
    return PRESS_SOURCES.map((source) => ({
      value: source,
      label: PRESS_SOURCE_LABELS[source][locale],
    }));
  }

  function getPressCategoryOptions(locale: SupportedLocale) {
    return PRESS_CATEGORIES.map((category) => ({
      value: category,
      label: PRESS_CATEGORY_LABELS[category][locale],
    }));
  }

  return (
    <>
      <Typography variant="overline" sx={{display: "block", mb: 1.5}}>
        Artikel filtern
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Quelle */}
        <FormControl size="small" sx={{minWidth: 200}}>
          <InputLabel id="source-label">Quelle</InputLabel>

          <Select
            labelId="source-label"
            label="Quelle"
            size="small"
            value={source ?? "ALL"}
            onChange={(e) =>
              update(
                e.target.value as PressSource | "ALL",
                category ?? "ALL"
              )
            }
          >
            <MenuItem value="ALL">Alle</MenuItem>

            {getPressSourceOptions(currentLocale).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Kategorie */}
        <FormControl size="small" sx={{minWidth: 200}}>
          <InputLabel id="category-label">Kategorie</InputLabel>

          <Select
            labelId="category-label"
            label="Kategorie"
            size="small"
            value={category ?? "ALL"}
            onChange={(e) =>
              update(
                source ?? "ALL",
                e.target.value as PressCategory | "ALL"
              )
            }
          >
            <MenuItem value="ALL">Alle</MenuItem>

            {getPressCategoryOptions(currentLocale).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isFiltered && (
          <Button
            startIcon={<ClearIcon/>}
            variant="text"
            onClick={() => update("ALL", "ALL")}
          >
            Filter zurücksetzen
          </Button>
        )}
      </Box>
    </>
  );
}
